// Eskom PARCC — working-paper notes renderer (owner R3).
//
// Turns the reviewer's semi-structured notes into a clean clause list (clause
// chip · criterion · labelled Result/Finding/Action, with the Result tone-coloured
// by status). Falls back to a well-typeset plain block when the text is not
// structured, and to a clamped one-line snippet in the compact row view — so the
// notes are never again squashed into a narrow ellipsised column.
import { parseWorkingPaperNotes, notesSnippet } from '../../lib/workingPaperNotes';
import { StatusIcon } from './StatusIcon';

export function WorkingPaperNotes({
  text,
  clamped = false,
}: {
  text: string | null | undefined;
  /** Compact one-line snippet for a row; full structured render otherwise. */
  clamped?: boolean;
}): React.JSX.Element {
  if (clamped) {
    const snippet = notesSnippet(text).trim();
    return <div className="wp-notes is-clamped">{snippet || '—'}</div>;
  }

  const parsed = parseWorkingPaperNotes(text);

  if (parsed.kind === 'plain') {
    return parsed.text ? (
      <div className="wp-note-plain">{parsed.text}</div>
    ) : (
      <span className="small">—</span>
    );
  }

  return (
    <div className="wp-notes">
      {parsed.items.map((it, i) => (
        <div className="wp-note-item" key={`${it.clause}-${i}`}>
          <span className="wp-note-clause">{it.clause}</span>
          {it.criterion && <div className="wp-note-criterion">{it.criterion}</div>}
          {it.result && (
            <div className="wp-note-field">
              <span className="wp-note-label">Result</span>
              <span className="status-cluster">
                <StatusIcon value={it.result} size={14} />
                {it.result}
              </span>
            </div>
          )}
          {it.finding && (
            <div className="wp-note-field">
              <span className="wp-note-label">Finding</span>
              {it.finding}
            </div>
          )}
          {it.action && (
            <div className="wp-note-field">
              <span className="wp-note-label">Action</span>
              {it.action}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
